/**
 * Player Events Logger (SE-4010)
 *
 * Handles logging of player-centric feature events to PostHog Analytics:
 * player detail views, comparison views, and similar-players views.
 * @module features/analytics/utils/playerEventLogger
 */
import { ANALYTICS_EVENTS } from "../constants";
import type {
  IPlayerAnalyticsService,
  TPlayerComparisonViewedParams,
  TPlayerProfileViewedParams,
  TSimilarPlayersViewedParams,
} from "../types";
import { sendAnalyticsEvent } from "./posthogSender";

/**
 * Player events analytics logger.
 *
 * Provides methods for logging engagement with player-centric surfaces.
 * @example
 * ```typescript
 * import { AnalyticsTracker } from '@/features/analytics';
 *
 * AnalyticsTracker.player.logPlayerProfileViewed({ player_id: 'player-123' });
 * ```
 */
export const playerEventLogger: IPlayerAnalyticsService = {
  /**
   * Logs a player profile viewed event.
   * @param params - Player profile viewed parameters
   */
  logPlayerProfileViewed: async (
    params: TPlayerProfileViewedParams
  ): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.PLAYER.PROFILE_VIEWED, params);
  },

  /**
   * Logs a player comparison viewed event.
   * @param params - Player comparison viewed parameters
   */
  logPlayerComparisonViewed: async (
    params: TPlayerComparisonViewedParams
  ): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.PLAYER.COMPARISON_VIEWED, params);
  },

  /**
   * Logs a similar-players viewed event.
   * @param params - Similar players viewed parameters
   */
  logSimilarPlayersViewed: async (
    params: TSimilarPlayersViewedParams
  ): Promise<void> => {
    await sendAnalyticsEvent(
      ANALYTICS_EVENTS.PLAYER.SIMILAR_PLAYERS_VIEWED,
      params
    );
  },
};
